import type { DateTime } from "luxon";
import { createContext, useContext } from "react";
import type { CoverageCellDisplayMode } from "../../../../components/UIToolkit/Coverage/CoverageDeltaCell";
import type { CoverageEditWiring } from "../CoverageGrid";
import type { RecurringShiftEditorRequest } from "../RecurringShiftEditor/CoverageRecurringShiftEditor";
import type { CellPopoverController } from "./useCellPopover";

/** A single grid cell address — the same shape for the Summary and Detail grids. */
export type GridCellKey = { rowId: string; dayIdx: number };

/** Stable string id for a cell key, so popover keys compare by value. */
export const serializeGridCell = (c: GridCellKey) => `${c.rowId}::${c.dayIdx}`;

/**
 * Grid-wide, read-only-per-render values shared by every row and cell of ONE
 * Master-Schedule v3 grid mount. Extracting these into context (instead of
 * threading them through ~14-prop rows) removes the grid-wide constants from
 * every row's prop list, so `React.memo`'d rows stay stable and unchanged rows
 * skip re-render. The value is memoized by the grid, so it only changes when one
 * of these actually changes (e.g. a popover opens) — never on hover.
 */
export type CoverageGridContextValue = {
	weeks: number;
	/** First day of week 1 (cycle anchor). */
	cycleStart: DateTime;
	/** Precomputed per-day dates (`weeks × 7`), so cells don't each re-derive them. */
	dayDates: DateTime[];
	/** How a Summary cell reads its number (Detail ignores this). */
	displayMode: CoverageCellDisplayMode;
	/** Location/assignment editing wiring (absent in pure prototype renders). */
	editWiring?: CoverageEditWiring;
	/** Single-open cell-popover controller (shared across the whole grid). */
	popover: CellPopoverController<GridCellKey>;
	/** Open the recurring-shift editor for a cell's day. */
	onOpenRecurring: (request: RecurringShiftEditorRequest, dayIdx: number) => void;
	/** Drive CSS linked hover (no React render) — set the hovered nurse or `null`. */
	setHoveredNurse: (nurseId: number | null) => void;
};

const CoverageGridContext = createContext<CoverageGridContextValue | null>(null);

export const CoverageGridProvider = CoverageGridContext.Provider;

/** Read the grid-wide values; throws if used outside a grid mount (a wiring bug). */
export const useCoverageGridContext = (): CoverageGridContextValue => {
	const ctx = useContext(CoverageGridContext);
	if (!ctx) throw new Error("useCoverageGridContext must be used within a CoverageGridProvider");
	return ctx;
};
